import {useEffect, useState} from 'react';
import {useHistory} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import {Box, Button} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import {translations} from '../../i18n/translations';
import {ExternalAccountType} from '../../backend/enums/external-account-type';
import {ExternalAccount} from '../../backend/interfaces/external-account';
import {CallExternalAccountData} from '../../backend/interfaces/call-external-account-data';
import {PartnerP3N1ExternalAccountData} from '../../backend/interfaces/partner-p3-n1-external-account-data';
import {PartnerP3N2ExternalAccountData} from '../../backend/interfaces/partner-p3-n2-external-account-data';
import {PartnerP3N3ExternalAccountData} from '../../backend/interfaces/partner-p3-n3-external-account-data';
import {PartnerP3N4ExternalAccountData} from '../../backend/interfaces/partner-p3-n4-external-account-data';
import {ExternalAccountsRepository} from '../../repositories/external-accounts';
import {getExternalAccountTypeLabel} from '../../utils/functions/get-external-account-type-label';
import {useSnackBar} from '../../contexts/snack-bar/context';
import {ContentPage} from '../content-page/content-page';
import {PageHeader} from '../page-header/page-header';
import {LoadingContent} from '../loading-content/loading-content';
import {ClipboardTextField} from '../clipboard-text-field/clipboard-text-field';
import DeleteResourceConfirmationModal from '../delete-resource-confirmation-modal';

/**
 * Read only details of an external account, with a delete button.
 *
 * There's no update path for external accounts, only create/delete.
 *
 * Only system administrators can access this screen.
 */
export const ExternalAccountDetails = (props: {
  userId: string;
  type: ExternalAccountType;
  redirectUrl: string;
}) => {
  const {t} = useTranslation();
  const snackBarContext = useSnackBar();
  let history = useHistory();

  const [account, setAccount] = useState<ExternalAccount<any> | null | undefined>(undefined);
  const [confirmDeleteOpened, setConfirmDeleteOpened] = useState<boolean>(false);
  const [isDeleting, setIsDeleting] = useState<boolean>(false);

  // On mount, get the account
  useEffect(() => {
    const getAccount = async () => {
      try {
        const accounts = await ExternalAccountsRepository.instance.getForUser(props.userId);
        const found = accounts.find((obj) => obj.type === props.type);

        if (found) {
          setAccount(found);
        } else {
          throw new Error('External account not found.');
        }
      } catch (error) {
        console.debug('🔴 Could not get the external account.', error);

        snackBarContext.setOfficeToastNotification({
          severity: 'error',
          message: t(translations.external_accounts.details.errors.not_found_message),
        });

        history.push(props.redirectUrl);
      }
    };

    getAccount();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.userId, props.type]);

  // Delete the account
  const handleDeleteAccount = async () => {
    setIsDeleting(true);

    try {
      await ExternalAccountsRepository.instance.delete(props.userId, props.type);

      snackBarContext.setOfficeToastNotification({
        severity: 'success',
        message: t(translations.external_accounts.list.delete_account_success_message, {
          type: getExternalAccountTypeLabel(props.type),
        }),
      });

      history.push(props.redirectUrl);
    } catch (error) {
      console.debug('🔴 Could not delete the external account.', error);

      snackBarContext.setOfficeToastNotification({
        severity: 'error',
        message: t(translations.external_accounts.list.delete_account_error_message, {
          type: getExternalAccountTypeLabel(props.type),
        }),
      });
    } finally {
      setIsDeleting(false);
      setConfirmDeleteOpened(false);
    }
  };

  // Render the fields according to the account type
  const renderFields = () => {
    if (!account) {
      return undefined;
    }

    const fields = translations.external_accounts.details.fields;

    switch (account.type) {
      case ExternalAccountType.Call: {
        const data: CallExternalAccountData = account.data;
        return (
          <>
            <ClipboardTextField label={t(fields.password)} value={data.password} />
            {data.applicationName && (
              <ClipboardTextField label={t(fields.application_name)} value={data.applicationName} />
            )}
          </>
        );
      }
      case ExternalAccountType.PartnerP3N1: {
        const data: PartnerP3N1ExternalAccountData = account.data;
        return (
          <>
            <ClipboardTextField label={t(fields.username)} value={data.username} />
            <ClipboardTextField label={t(fields.password)} value={data.password} />
            <ClipboardTextField label={t(fields.language)} value={data.language} />
            <ClipboardTextField
              label={t(fields.project_identification)}
              value={data.projectIdentification}
            />
          </>
        );
      }
      case ExternalAccountType.PartnerP3N2: {
        const data: PartnerP3N2ExternalAccountData = account.data;
        return (
          <>
            <ClipboardTextField label={t(fields.username)} value={data.username} />
            <ClipboardTextField label={t(fields.password)} value={data.password} />
            <ClipboardTextField label={t(fields.serial)} value={data.serial} />
            <ClipboardTextField label={t(fields.language)} value={data.language} />
            <ClipboardTextField label={t(fields.profile)} value={data.profile} />
            <ClipboardTextField label={t(fields.timezone)} value={data.timezone} />
            <ClipboardTextField label={t(fields.institution_id)} value={data.institutionId} />
            {data.instanceName && (
              <ClipboardTextField label={t(fields.instance_name)} value={data.instanceName} />
            )}
            {data.manufacturer && (
              <ClipboardTextField label={t(fields.manufacturer)} value={data.manufacturer} />
            )}
            {data.model && <ClipboardTextField label={t(fields.model)} value={data.model} />}
          </>
        );
      }
      case ExternalAccountType.PartnerP3N3: {
        const data: PartnerP3N3ExternalAccountData = account.data;
        return <ClipboardTextField label={t(fields.user_id)} value={data.userId} />;
      }
      case ExternalAccountType.PartnerP3N4: {
        const data: PartnerP3N4ExternalAccountData = account.data;
        return (
          <>
            <ClipboardTextField label={t(fields.patient_id)} value={data.patientId} />
            {data.email && <ClipboardTextField label={t(fields.email)} value={data.email} />}
            {data.password && (
              <ClipboardTextField label={t(fields.password)} value={data.password} />
            )}
          </>
        );
      }
      default:
        return undefined;
    }
  };

  return (
    <ContentPage>
      {!account && <LoadingContent marginTop="32px" />}
      {account && (
        <>
          <PageHeader
            title={getExternalAccountTypeLabel(props.type)}
            isSmallTitle={true}
            rightButtons={
              <Button
                variant="outlined"
                color="error"
                startIcon={<DeleteIcon />}
                onClick={() => setConfirmDeleteOpened(true)}
              >
                {t(translations.external_accounts.details.delete_button)}
              </Button>
            }
          />
          <Box display="flex" flexDirection="column" gap="12px">
            {renderFields()}
          </Box>
          <DeleteResourceConfirmationModal
            id="confirm-delete-external-account-details-dialog"
            open={confirmDeleteOpened}
            title={t(translations.external_accounts.list.delete_account_dialog_title, {
              type: getExternalAccountTypeLabel(props.type),
            })}
            description={t(translations.external_accounts.list.delete_account_dialog_description)}
            cannotBeUndoneMessage={t(
              translations.external_accounts.list.delete_account_dialog_cannot_be_undone
            )}
            confirm_label={t(
              translations.external_accounts.list.delete_account_dialog_confirm_button
            )}
            cancel_label={t(
              translations.external_accounts.list.delete_account_dialog_cancel_button
            )}
            isDeleting={isDeleting}
            onCancel={() => setConfirmDeleteOpened(false)}
            onConfirm={handleDeleteAccount}
          />
        </>
      )}
    </ContentPage>
  );
};
